import { useRef, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Body,
  Button,
  Card,
  Eyebrow,
  Field,
  Heading,
  I,
  PageHeader,
  Row,
  Screen,
  ScreenBody,
  Select,
  Stack,
} from '@leanin/ui';
import { REGIONS, type Region } from '@leanin/shared/region';
import { RouterLinkAs } from '@/lib/router-link';
import { BodyWithAside } from '@/components/page-shell';
import {
  AdminImagePanel,
  type AdminImagePanelHandle,
} from '@/components/group/admin/AdminImagePanel';
import {
  NetworkProfileFields,
  EMPTY_SOCIAL_DRAFT,
  socialDraftToPayload,
  type NetworkProfileDraft,
} from '@/components/networks/admin/NetworkProfileFields';
import { useCreateNetwork } from '@/lib/mutations/groups';
import { requirePlatformRole } from '@/lib/route-guards';
import { failureFromError } from '@/lib/api-error';

// Platform-admin-only network provisioning. The `_authed` parent guarantees a
// session; here we narrow to the `admin` role and bounce everyone else back to
// the directory. Network leaders apply via /networks/new (the Network Leaders
// program); this surface is the staff tool that mints the actual page.
export const Route = createFileRoute('/_authed/networks/create')({
  beforeLoad: async ({ context }) => {
    await requirePlatformRole(context, {
      role: 'admin',
      bounceTo: () => redirect({ to: '/networks' }),
    });
  },
  component: NetworkCreatePage,
});

type Access = 'public' | 'unlisted' | 'private';
type MinReadRole = 'anyone' | 'member' | 'leader';

const ACCESS_OPTIONS: ReadonlyArray<{ value: Access; label: string }> = [
  { value: 'public', label: 'Public — listed in the Networks directory' },
  { value: 'unlisted', label: 'Unlisted — hidden from the directory, link works' },
  { value: 'private', label: 'Private — only members can find it' },
];

const READ_ROLE_OPTIONS: ReadonlyArray<{ value: MinReadRole; label: string }> = [
  { value: 'anyone', label: 'Anyone — including signed-out visitors' },
  { value: 'member', label: 'Members — must join to view the page' },
  { value: 'leader', label: 'Leaders only' },
];

const EMPTY_DRAFT: NetworkProfileDraft = {
  name: '',
  tagline: '',
  region: REGIONS[0] as Region,
  mission: '',
  description: '',
  welcomeMessage: '',
  joinInstructions: '',
  tags: [],
  social: EMPTY_SOCIAL_DRAFT,
};

const TIPS: ReadonlyArray<{ t: string; s: string }> = [
  {
    t: 'Name it consistently',
    s: 'Lean In Networks follow "Lean In Network, <City>" — e.g. "Lean In Network, Singapore". Networks aren\'t named after countries or regions.',
  },
  {
    t: 'Set the page up to convert',
    s: 'Mission, welcome, and join instructions all surface on the public Overview tab. A complete profile is what turns a visitor into a member request.',
  },
  {
    t: 'You can edit anything later',
    s: 'Everything here is editable from the Network admin Overview once the page exists. Publish a solid first version — you don\'t have to be perfect.',
  },
  {
    t: 'Visibility',
    s: 'Public + viewable by anyone lists the Network in the directory and shows the page to signed-out visitors immediately. Tighten it only if you need a soft launch.',
  },
];

function NetworkCreatePage() {
  const navigate = useNavigate();
  const createNetwork = useCreateNetwork();

  const [draft, setDraft] = useState<NetworkProfileDraft>(EMPTY_DRAFT);
  const [access, setAccess] = useState<Access>('public');
  const [minReadRole, setMinReadRole] = useState<MinReadRole>('anyone');

  const imagesRef = useRef<AdminImagePanelHandle | null>(null);
  const [imagesPending, setImagesPending] = useState(false);
  const [committingImages, setCommittingImages] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = (p: Partial<NetworkProfileDraft>) =>
    setDraft((d) => ({ ...d, ...p }));

  // Server bounds: name min 3, description min 1.
  const canSubmit =
    draft.name.trim().length >= 3 && draft.description.trim().length >= 1;

  const isPending = createNetwork.isPending || committingImages;

  const onSubmit = async () => {
    setSubmitError(null);
    if (!canSubmit) {
      setSubmitError(
        'A name (3+ characters) and a description are required before publishing.',
      );
      return;
    }

    const socials = socialDraftToPayload(draft.social);
    let detail;
    try {
      detail = await createNetwork.mutateAsync({
        name: draft.name.trim(),
        description: draft.description.trim(),
        region: draft.region,
        access,
        minReadRole,
        tagline: draft.tagline.trim() || undefined,
        mission: draft.mission.trim() || undefined,
        welcomeMessage: draft.welcomeMessage.trim() || undefined,
        joinInstructions: draft.joinInstructions.trim() || undefined,
        tags: draft.tags.length > 0 ? draft.tags : undefined,
        socialLinks: Object.keys(socials).length > 0 ? socials : undefined,
      });
    } catch (err) {
      setSubmitError(failureFromError(err, 'Failed to create Network.').message);
      return;
    }

    // Images are optional and committed under the freshly-created id. A failed
    // upload doesn't strand the admin — the Network exists; surface the
    // message and route forward so they can retry from the admin Overview.
    if (imagesPending) {
      setCommittingImages(true);
      try {
        await imagesRef.current?.commit(detail.id);
      } catch (err) {
        setSubmitError(
          failureFromError(
            err,
            'Network created, but an image upload failed. You can retry from Network settings.',
          ).message,
        );
      } finally {
        setCommittingImages(false);
      }
    }

    navigate({ to: '/networks/$id', params: { id: detail.id } });
  };

  return (
    <Screen>
      <ScreenBody gap="xl">
        <PageHeader
          eyebrow="New Network"
          title="Create a Network"
          subtitle="Provision a Network page. Everything you enter here surfaces on the public Network and is editable later from the Network admin."
          back={{ label: 'Networks', href: '/networks' }}
          actions={
            <>
              <Button href="/networks" linkAs={RouterLinkAs} variant="ghost">
                Cancel
              </Button>
              <Button
                variant="primary"
                iconRight={I.arrow}
                onClick={onSubmit}
                disabled={!canSubmit}
                loading={isPending}
              >
                Publish Network
              </Button>
            </>
          }
        />
        <BodyWithAside
          asideWidth={380}
          aside={
            <Card pad="default">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow tone="accent">Good to know</Eyebrow>
                  <Heading level={3}>Setting up the page</Heading>
                </Stack>
                <Stack gap="md">
                  {TIPS.map((tip) => (
                    <Stack key={tip.t} gap="xs">
                      <Heading level={5}>{tip.t}</Heading>
                      <Body size="sm" color="mute">
                        {tip.s}
                      </Body>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Card>
          }
        >
          <Stack gap="lg">
            <Card pad="flush">
              <AdminImagePanel
                ref={imagesRef}
                groupKind="network"
                // Placeholder id — AdminImagePanel doesn't presign until
                // commit(), and Publish passes the new Network's id as the
                // override at that point.
                groupId="pending"
                groupName={draft.name || 'Your Network'}
                currentCoverUrl={null}
                currentLogoUrl={null}
                onPendingChange={setImagesPending}
              />
              <NetworkProfileFields value={draft} onChange={patch} />
            </Card>

            <Card pad="spacious">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Eyebrow tone="accent">Visibility</Eyebrow>
                  <Body size="sm" color="mute">
                    Controls who can find the Network and who can view its page.
                  </Body>
                </Stack>
                <Row gap="md" wrap align="flex-start">
                  <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Field label="Directory listing">
                      <Select
                        value={access}
                        options={ACCESS_OPTIONS}
                        onChange={(e) => setAccess(e.target.value as Access)}
                      />
                    </Field>
                  </Stack>
                  <Stack gap="sm" style={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Field label="Who can view the page">
                      <Select
                        value={minReadRole}
                        options={READ_ROLE_OPTIONS}
                        onChange={(e) =>
                          setMinReadRole(e.target.value as MinReadRole)
                        }
                      />
                    </Field>
                  </Stack>
                </Row>
              </Stack>
            </Card>

            {submitError !== null ? (
              <Card pad="default" tone="burgundy">
                <Body color="primary">{submitError}</Body>
              </Card>
            ) : null}

            <Row justify="space-between" align="center">
              <Body size="sm" color="mute">
                You can edit any of this after publishing.
              </Body>
              <Row gap="sm">
                <Button href="/networks" linkAs={RouterLinkAs} variant="ghost">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  iconRight={I.arrow}
                  onClick={onSubmit}
                  disabled={!canSubmit}
                  loading={isPending}
                >
                  Publish Network
                </Button>
              </Row>
            </Row>
          </Stack>
        </BodyWithAside>
      </ScreenBody>
    </Screen>
  );
}
